/**
 * Meeting Session Manager
 * Manages unique session IDs for each meeting training attempt
 *
 * Each time a user enters a meeting, a new UUID is generated to track that specific training session.
 * This allows the backend to differentiate between multiple attempts of the same meeting.
 */
import { logger } from "@/utils/logger"
/**
 * Meeting metadata stored alongside session
 */
interface MeetingMetadata {
  name: string;
  category: string;
}

/**
 * Generate storage key for meeting session
 */
const getStorageKey = (meetingId: string): string => {
  return `meeting_session_${meetingId}`;
};

/**
 * Meeting Session Manager
 * Handles creation, retrieval, and cleanup of meeting session UUIDs
 */
export const MeetingSessionManager = {
  /**
   * Create a new meeting session UUID
   * This should be called when user enters /meeting/{id}
   *
   * @param meetingId - The meeting ID
   * @returns The generated UUID v4
   */
  create(meetingId: string): string {
    const sessionId = crypto.randomUUID(); // Generate UUID v4
    const key = getStorageKey(meetingId);

    logger.log("[MeetingSessionManager] Creating new session:", {
      meetingId,
      sessionId,
    });

    sessionStorage.setItem(key, sessionId);
    return sessionId;
  },

  /**
   * Get existing meeting session UUID
   * Returns null if no session exists for this meeting
   *
   * @param meetingId - The meeting ID
   * @returns The session UUID or null
   */
  get(meetingId: string): string | null {
    const key = getStorageKey(meetingId);
    const sessionId = sessionStorage.getItem(key);

    if (!sessionId) {
      logger.warn(
        "[MeetingSessionManager] No session found for meeting:",
        meetingId
      );
      logger.warn("[MeetingSessionManager] Storage key:", key);
      logger.warn(
        "[MeetingSessionManager] All sessionStorage keys:",
        Object.keys(sessionStorage)
      );

      // Debug: List all meeting sessions
      const allKeys = Object.keys(sessionStorage);
      const meetingSessions = allKeys.filter((k) =>
        k.startsWith("meeting_session_")
      );
      logger.warn(
        "[MeetingSessionManager] Existing meeting sessions:",
        meetingSessions.map((k) => ({
          key: k,
          value: sessionStorage.getItem(k),
        }))
      );
    } else {
      logger.log("[MeetingSessionManager] Found session:", {
        meetingId,
        sessionId,
        key,
      });
    }

    return sessionId;
  },

  /**
   * Get or create meeting session UUID
   * Convenience method that gets existing session or creates new one
   *
   * @param meetingId - The meeting ID
   * @returns The session UUID
   */
  getOrCreate(meetingId: string): string {
    const existing = this.get(meetingId);
    if (existing) {
      return existing;
    }
    return this.create(meetingId);
  },

  /**
   * Clear meeting session UUID
   * Should be called when user exits the meeting
   *
   * @param meetingId - The meeting ID
   */
  clear(meetingId: string): void {
    const key = getStorageKey(meetingId);
    const sessionId = sessionStorage.getItem(key);

    logger.log("[MeetingSessionManager] Clearing session:", {
      meetingId,
      sessionId,
    });

    sessionStorage.removeItem(key);
    sessionStorage.removeItem(`meeting_metadata_${meetingId}`);
  },

  /**
   * Check if a session exists for a meeting
   *
   * @param meetingId - The meeting ID
   * @returns True if session exists
   */
  exists(meetingId: string): boolean {
    return this.get(meetingId) !== null;
  },

  /**
   * Store meeting metadata (name, category)
   * Should be called when meeting data is loaded
   *
   * @param meetingId - The meeting ID
   * @param metadata - Meeting name and category
   */
  setMetadata(meetingId: string, metadata: MeetingMetadata): void {
    const key = `meeting_metadata_${meetingId}`;
    sessionStorage.setItem(key, JSON.stringify(metadata));
    logger.log("[MeetingSessionManager] Stored metadata:", {
      meetingId,
      metadata,
    });
  },

  /**
   * Get meeting metadata
   * Returns null if no metadata exists for this meeting
   *
   * @param meetingId - The meeting ID
   * @returns The meeting metadata or null
   */
  getMetadata(meetingId: string): MeetingMetadata | null {
    const key = `meeting_metadata_${meetingId}`;
    const data = sessionStorage.getItem(key);

    if (!data) {
      logger.warn("[MeetingSessionManager] No metadata found for:", meetingId);
      return null;
    }

    try {
      return JSON.parse(data);
    } catch (error) {
      logger.error("[MeetingSessionManager] Failed to parse metadata:", error);
      return null;
    }
  },
};
